export const WIZARD_STEPS = [
    { id: 1, label: "Choose Products" },
    { id: 2, label: "Select Plans" },
    { id: 3, label: "Review & Pay" }
];

export const initialStep = ({ initialProductId, initialProductCategoryId, category }) => (
    initialProductId || initialProductCategoryId || category ? 2 : 1
);

export const initialProduct = ({ initialProductId, initialProductCategoryId, category }) => (
    initialProductId || initialProductCategoryId || category || null
);

export const normalizeCatalog = (payload) => Object.values(payload?.products || {})
    .filter(product => product?.category && Array.isArray(product.plans) && product.plans.length > 0)
    .map(product => {
        const plans = product.plans.map(plan => ({
            ...plan,
            id: plan.uuid,
            productId: product.category,
            priceCents: Math.round(Number(plan.price || 0) * 100)
        }));
        const purchasable = plans.filter(plan => !plan.custom);
        const startingPriceCents = purchasable.length > 0
            ? Math.min(...purchasable.map(plan => plan.priceCents))
            : null;
        return {
            ...product,
            id: product.category,
            description: product.description || `Flexible ${product.metric || "service"} plans for your organization.`,
            plans,
            startingPriceCents,
            freeAvailable: purchasable.some(plan => plan.priceCents === 0)
        };
    });

export const selectedProductIdsFor = (props, catalog = []) => {
    const selected = initialProduct(props);
    return selected && catalog.some(product => product.id === selected) ? [selected] : [];
};

export const applyFreeLicenseLimits = (products, licenses, { existingLicenseId = null, licenseeUuid = null, now = Date.now() } = {}) => {
    if (existingLicenseId) return products;
    const categories = new Set(licenses.filter(license => {
        if (licenseeUuid && license.licenseeUuid && license.licenseeUuid !== licenseeUuid) return false;
        const price = license["Product.price"] ?? license.Product?.price;
        return price !== undefined && Number(price) === 0
            && String(license.status).toLowerCase() === "complete"
            && (!license.endDate || new Date(license.endDate).getTime() > now);
    }).map(license => license["Product.category"] || license.Product?.category).filter(Boolean));
    return products.map(product => ({
        ...product,
        plans: product.plans.map(plan => categories.has(product.id) && !plan.custom && plan.priceCents === 0
            ? { ...plan, disabled: true, disabledReason: "Free license already active" }
            : plan)
    }));
};

export const addProduct = (selectedIds, productId) => (
    selectedIds.includes(productId) ? selectedIds : [...selectedIds, productId]
);

export const removeProduct = (state, productId) => {
    const selectedPlanByProductId = { ...state.selectedPlanByProductId };
    const planErrors = { ...(state.errors?.plans || {}) };
    delete selectedPlanByProductId[productId];
    delete planErrors[productId];
    return {
        ...state,
        selectedProductIds: state.selectedProductIds.filter(id => id !== productId),
        selectedPlanByProductId,
        estimate: null,
        errors: {
            ...state.errors,
            plans: planErrors
        }
    };
};

export const chooseDefaultPlan = (plans = [], currentPlanId = null) => {
    const current = plans.find(plan => plan.id === currentPlanId && !plan.disabled);
    if (current) return current.id;

    const freeDefault = plans.find(plan => !plan.custom && !plan.disabled && plan.default && plan.priceCents === 0);
    if (freeDefault) return freeDefault.id;
    const free = plans.find(plan => !plan.custom && !plan.disabled && plan.priceCents === 0);
    return free?.id || null;
};

export const hydratePlanSelections = ({ products, selectedProductIds, previous = {}, currentPlanId = null }) => {
    const next = {};
    selectedProductIds.forEach(productId => {
        const product = products.find(item => item.id === productId);
        const plans = product?.plans || [];
        if (plans.some(plan => plan.id === previous[productId] && !plan.custom && !plan.disabled)) {
            next[productId] = previous[productId];
        } else {
            next[productId] = chooseDefaultPlan(plans, currentPlanId);
        }
    });
    return next;
};

export const validateStepOne = selectedProductIds => selectedProductIds.length > 0;

export const planErrors = ({ selectedProductIds, selectedPlanByProductId, productCatalog = [] }) => Object.fromEntries(
    selectedProductIds
        .filter(productId => !selectedPlanByProductId[productId]
            || productCatalog.find(product => product.id === productId)?.plans
                .find(plan => plan.id === selectedPlanByProductId[productId])?.disabled)
        .map(productId => [productId, "Choose a plan for this product."])
);

export const validateStepTwo = state => Object.keys(planErrors(state)).length === 0;

export const orderSelections = state => state.selectedProductIds.map(productId => ({
    productId,
    planId: state.selectedPlanByProductId[productId]
}));

export const formatCents = (amount, currency = "USD") => new Intl.NumberFormat("en-US", {
    style: "currency",
    currency
}).format(Number(amount || 0) / 100);

export const frequencyLabel = frequency => ({
    annual: "Annual",
    monthly: "Monthly",
    one_time: "One time",
    A: "Annual",
    M: "Monthly",
    P: "Forever"
}[frequency] || "Plan");

